/**
 * Every state an operator sees, mapped to plain UI copy and one of three tones:
 *   neutral  ink dot: the ordinary, current state
 *   faint    hollow dot, muted text: finished, inactive or historical
 *   accent   accent dot: needs a human now (review, failure, uncertainty)
 * Enum values come from the API contract; labels are UI copy only.
 */
export type Tone = 'neutral' | 'faint' | 'accent';

export interface StatusDef {
  label: string;
  tone: Tone;
  /** Longer explanation, shown as the accessible description / title. */
  hint?: string;
}

type Domain = Record<string, StatusDef>;

export const STATUS_DOMAINS = {
  pipeline: {
    ingested: { label: 'New', tone: 'neutral' },
    researching: { label: 'Researching', tone: 'neutral' },
    enriching: { label: 'Enriching', tone: 'neutral' },
    engaged: { label: 'Engaged', tone: 'neutral' },
    ready_for_serious_engagement: { label: 'Ready', tone: 'accent', hint: 'Ready for serious engagement' },
    disqualified: { label: 'Disqualified', tone: 'faint' },
    cancelled: { label: 'Cancelled', tone: 'faint' },
    archived: { label: 'Archived', tone: 'faint' },
  },
  automation: {
    inherit: { label: 'Default', tone: 'neutral', hint: 'Uses the organization default' },
    automatic: { label: 'Automatic', tone: 'neutral' },
    manual: { label: 'Manual', tone: 'neutral' },
    paused: { label: 'Paused', tone: 'faint' },
  },
  handoff: {
    none: { label: 'No handoff', tone: 'faint' },
    manual_only: {
      label: 'Handed off',
      tone: 'neutral',
      hint: 'A person handles correspondence; research can continue.',
    },
  },
  conversation: {
    draft_container: { label: 'Not sent yet', tone: 'faint', hint: 'No mail sent' },
    inbound_only: { label: 'Received', tone: 'neutral', hint: 'Received first; nothing sent yet' },
    opened: { label: 'Open', tone: 'neutral' },
    closed: { label: 'Closed', tone: 'faint' },
    archived: { label: 'Archived', tone: 'faint' },
  },
  draft: {
    draft: { label: 'Draft', tone: 'neutral', hint: 'No mail sent' },
    pending_review: { label: 'To approve', tone: 'accent', hint: 'No mail sent' },
    approved: { label: 'Approved', tone: 'neutral', hint: 'No mail sent yet' },
    scheduled: { label: 'Sending soon', tone: 'neutral', hint: 'Sends at its due time unless cancelled' },
    sending: { label: 'Sending', tone: 'neutral', hint: 'Submission may be in progress' },
    sent: { label: 'Sent', tone: 'faint' },
    revoked: { label: 'Cancelled', tone: 'faint' },
    failed: { label: 'Failed', tone: 'accent' },
    cancelled: { label: 'Discarded', tone: 'faint' },
    delivery_unknown: {
      label: 'Delivery unknown',
      tone: 'accent',
      hint: 'This may already have been sent. Do not resend blindly.',
    },
  },
  delivery: {
    scheduled: { label: 'Sending soon', tone: 'neutral' },
    sending: { label: 'Sending', tone: 'neutral' },
    sent: { label: 'Sent', tone: 'faint' },
    revoked: { label: 'Cancelled', tone: 'faint' },
    failed: { label: 'Failed', tone: 'accent' },
    cancelled: { label: 'Cancelled', tone: 'faint' },
    delivery_unknown: { label: 'Delivery unknown', tone: 'accent', hint: 'This may already have been sent.' },
  },
  attempt: {
    accepted: { label: 'Accepted', tone: 'neutral' },
    definitely_not_submitted: { label: 'Not submitted', tone: 'faint' },
    rejected: { label: 'Rejected', tone: 'accent' },
    unknown: { label: 'Unknown', tone: 'accent' },
  },
  purpose: {
    automatic: { label: 'Automatic', tone: 'neutral' },
    human_manual: { label: 'Manual', tone: 'neutral' },
  },
  draftOrigin: {
    manual: { label: 'Person', tone: 'neutral' },
    simple_llm: { label: 'AI', tone: 'neutral' },
    agent: { label: 'AI agent', tone: 'neutral' },
    import: { label: 'Imported', tone: 'faint' },
  },
  messageKind: {
    inbound_email: { label: 'Received', tone: 'neutral' },
    outbound_email: { label: 'Sent', tone: 'neutral' },
    manual: { label: 'Note', tone: 'faint', hint: 'Recorded by a person. No email was sent.' },
  },
  recordOrigin: {
    platform: { label: 'Sent from Eryce', tone: 'neutral' },
    mailbox_capture: { label: 'Mailbox', tone: 'neutral' },
    imported_history: { label: 'Imported', tone: 'faint' },
    manual_entry: { label: 'Entered by hand', tone: 'faint' },
  },
  senderType: {
    owner_operator: { label: 'Owner or operator', tone: 'neutral' },
    broker_adviser: { label: 'Broker or adviser', tone: 'neutral' },
    insolvency_seller: { label: 'Insolvency seller', tone: 'neutral' },
    municipality: { label: 'Municipality', tone: 'neutral' },
    utility: { label: 'Utility', tone: 'neutral' },
    other: { label: 'Other', tone: 'neutral' },
  },
  job: {
    pending: { label: 'Waiting', tone: 'neutral' },
    leased: { label: 'Running', tone: 'neutral' },
    succeeded: { label: 'Done', tone: 'faint' },
    failed: { label: 'Failed', tone: 'accent' },
    cancelled: { label: 'Cancelled', tone: 'faint' },
    blocked: { label: 'Blocked', tone: 'accent' },
  },
  run: {
    queued: { label: 'Queued', tone: 'neutral' },
    running: { label: 'Running', tone: 'neutral' },
    completed: { label: 'Done', tone: 'faint' },
    failed: { label: 'Failed', tone: 'accent' },
    cancelled: { label: 'Cancelled', tone: 'faint' },
    checkpoint_lost: { label: 'Lost progress', tone: 'accent' },
  },
  session: {
    active: { label: 'Active', tone: 'neutral' },
    paused: { label: 'Paused', tone: 'faint' },
    checkpoint_lost: { label: 'Lost progress', tone: 'accent' },
    retired: { label: 'Retired', tone: 'faint' },
  },
  readiness: {
    ready: { label: 'Ready', tone: 'neutral' },
    not_ready: { label: 'Not ready', tone: 'faint' },
    uncertain: { label: 'Unclear', tone: 'neutral' },
  },
  role: {
    admin: { label: 'Admin', tone: 'neutral' },
    user: { label: 'Member', tone: 'neutral' },
  },
  membership: {
    invited: { label: 'Invited', tone: 'neutral' },
    active: { label: 'Active', tone: 'neutral' },
    suspended: { label: 'Suspended', tone: 'faint' },
    removed: { label: 'Removed', tone: 'faint' },
  },
  actor: {
    human: { label: 'Person', tone: 'neutral' },
    agent: { label: 'Agent', tone: 'neutral' },
    connector: { label: 'Connector', tone: 'neutral' },
    runner: { label: 'Runner', tone: 'neutral' },
    system: { label: 'System', tone: 'faint' },
    migration: { label: 'Import', tone: 'faint' },
  },
  connector: {
    active: { label: 'Active', tone: 'neutral' },
    paused: { label: 'Paused', tone: 'faint' },
    revoked: { label: 'Revoked', tone: 'faint' },
  },
  notificationDelivery: {
    pending: { label: 'Waiting', tone: 'neutral' },
    delivered: { label: 'Delivered', tone: 'faint' },
    failed: { label: 'Failed', tone: 'accent' },
    cancelled: { label: 'Cancelled', tone: 'faint' },
  },
  severity: {
    info: { label: 'Info', tone: 'faint' },
    warning: { label: 'Warning', tone: 'neutral' },
    blocking: { label: 'Blocking', tone: 'accent' },
  },
  redFlag: {
    active: { label: 'Red flag', tone: 'accent' },
    dismissed: { label: 'Dismissed', tone: 'faint' },
    resolved: { label: 'Resolved', tone: 'faint' },
  },
  factVerification: {
    reported: { label: 'Reported', tone: 'neutral' },
    corroborated: { label: 'Confirmed', tone: 'neutral' },
    disputed: { label: 'Disputed', tone: 'accent' },
    retracted: { label: 'Retracted', tone: 'faint' },
  },
  confidence: {
    unknown: { label: 'Unknown confidence', tone: 'faint' },
    low: { label: 'Low', tone: 'faint' },
    medium: { label: 'Medium', tone: 'neutral' },
    high: { label: 'High', tone: 'neutral' },
  },
  assertion: {
    proposed: { label: 'Proposed', tone: 'neutral' },
    accepted: { label: 'Accepted', tone: 'neutral' },
    conflicting: { label: 'Conflicting', tone: 'accent' },
    rejected: { label: 'Rejected', tone: 'faint' },
  },
  blob: {
    uploading: { label: 'Uploading', tone: 'neutral' },
    quarantined: { label: 'Being checked', tone: 'neutral' },
    clean: { label: 'Ready', tone: 'neutral' },
    rejected: { label: 'Rejected', tone: 'accent' },
    unavailable: { label: 'Unavailable', tone: 'faint' },
  },
  municipal: {
    unknown: { label: 'Unknown', tone: 'faint' },
    not_required: { label: 'Not required', tone: 'faint' },
    needed: { label: 'Needed', tone: 'neutral' },
    applied: { label: 'Applied', tone: 'neutral' },
    approved: { label: 'Approved', tone: 'neutral' },
    rejected: { label: 'Rejected', tone: 'accent' },
    expired: { label: 'Expired', tone: 'accent' },
  },
  grid: {
    unknown: { label: 'Unknown', tone: 'faint' },
    none: { label: 'None', tone: 'faint' },
    requested: { label: 'Requested', tone: 'neutral' },
    offered: { label: 'Offered', tone: 'neutral' },
    contracted: { label: 'Contracted', tone: 'neutral' },
    energized: { label: 'Energized', tone: 'neutral' },
  },
  contactStatus: {
    active: { label: 'Active', tone: 'neutral' },
    do_not_contact: { label: 'Do not contact', tone: 'accent' },
    unverified: { label: 'Unverified', tone: 'faint' },
    archived: { label: 'Archived', tone: 'faint' },
  },
  verification: {
    unverified: { label: 'Unverified', tone: 'faint' },
    verified: { label: 'Verified', tone: 'neutral' },
    invalid: { label: 'Invalid', tone: 'accent' },
  },
  resolutionCase: {
    open: { label: 'Unassigned', tone: 'accent' },
    resolved: { label: 'Assigned', tone: 'faint' },
    dismissed: { label: 'Dismissed', tone: 'faint' },
  },
  apiKey: {
    active: { label: 'Active', tone: 'neutral' },
    revoked: { label: 'Revoked', tone: 'faint' },
    expired: { label: 'Expired', tone: 'faint' },
  },
  migrationBatch: {
    planned: { label: 'Planned', tone: 'neutral' },
    staged: { label: 'Staged', tone: 'neutral' },
    validated: { label: 'Checked', tone: 'neutral' },
    applied: { label: 'Applied', tone: 'faint' },
    reconciled: { label: 'Reconciled', tone: 'faint' },
    rejected: { label: 'Rejected', tone: 'accent' },
  },
  publication: {
    draft: { label: 'Draft', tone: 'neutral' },
    published: { label: 'Published', tone: 'neutral' },
    withdrawn: { label: 'Withdrawn', tone: 'faint' },
  },
  access: {
    allow: { label: 'Allowed', tone: 'neutral' },
    deny: { label: 'Denied', tone: 'accent' },
  },
} satisfies Record<string, Domain>;

export type StatusDomain = keyof typeof STATUS_DOMAINS;

export const DOMAIN_LABELS: Record<StatusDomain, string> = {
  pipeline: 'Stage',
  automation: 'Automation',
  handoff: 'Handoff',
  conversation: 'Conversation',
  draft: 'Draft',
  delivery: 'Delivery',
  attempt: 'Attempt',
  purpose: 'Purpose',
  draftOrigin: 'Written by',
  messageKind: 'Message',
  recordOrigin: 'Source',
  senderType: 'Sender',
  job: 'Job',
  run: 'Run',
  session: 'Session',
  readiness: 'Readiness',
  role: 'Role',
  membership: 'Membership',
  actor: 'Actor',
  connector: 'Connector',
  notificationDelivery: 'Delivery',
  severity: 'Severity',
  redFlag: 'Red flag',
  factVerification: 'Verification',
  confidence: 'Confidence',
  assertion: 'Assertion',
  blob: 'File',
  municipal: 'Municipal',
  grid: 'Grid',
  contactStatus: 'Contact',
  verification: 'Address',
  resolutionCase: 'Routing',
  apiKey: 'Key',
  migrationBatch: 'Batch',
  publication: 'Release',
  access: 'Access',
};

export const PIPELINE_ORDER = [
  'ingested',
  'researching',
  'enriching',
  'engaged',
  'ready_for_serious_engagement',
  'disqualified',
  'cancelled',
  'archived',
] as const;
export type PipelineState = (typeof PIPELINE_ORDER)[number];

export const SENDER_TYPES = [
  'owner_operator',
  'broker_adviser',
  'insolvency_seller',
  'municipality',
  'utility',
  'other',
] as const;

export function statusDef(domain: StatusDomain, value: string | null | undefined): StatusDef {
  if (!value) return { label: 'Unknown', tone: 'faint' };
  const d = (STATUS_DOMAINS[domain] as Domain)[value];
  return d ?? { label: value.replace(/_/g, ' '), tone: 'neutral', hint: `Unrecognised value "${value}"` };
}

export function domainOptions(domain: StatusDomain): Array<{ value: string; label: string }> {
  return Object.entries(STATUS_DOMAINS[domain] as Domain).map(([value, def]) => ({ value, label: def.label }));
}
